import images from "../common/images";
import { BASE_URL } from "../services/apis";

const fallbackImage = images.hero;

const apiBase = String(BASE_URL || "").replace(/\/$/, "");

/** Resolve relative storage paths to absolute API URLs */
export function resolveMediaUrl(src) {
  if (!src) return "";
  if (typeof src !== "string") {
    return resolveMediaUrl(src.path || src.url || src.fullUrl || src.src || "");
  }
  if (src.startsWith("data:") || src.startsWith("blob:")) {
    return src;
  }
  if (src.startsWith("http://") || src.startsWith("https://")) {
    try {
      const url = new URL(src);
      const isLocalHost =
        url.hostname === "localhost" ||
        url.hostname === "127.0.0.1" ||
        url.hostname === "0.0.0.0";
      if (isLocalHost && apiBase) {
        return `${apiBase}${url.pathname}${url.search}`;
      }
    } catch {
      // keep original
    }
    return src;
  }
  if (src.startsWith("/")) return `${apiBase}${src}`;
  return `${apiBase}/${src}`;
}

function resolveImageEntry(entry) {
  if (!entry) return "";
  if (typeof entry === "string" && entry.trim()) {
    return resolveMediaUrl(entry);
  }
  const fromAttachment =
    entry.path || entry.url || entry.fullUrl || entry.src || entry.image;
  return fromAttachment ? resolveMediaUrl(fromAttachment) : "";
}

function pickBlogImages(raw) {
  const collected = [];
  const pushUnique = (url) => {
    if (url && !collected.includes(url)) collected.push(url);
  };

  if (typeof raw?.image === "string" && raw.image.trim()) {
    pushUnique(resolveMediaUrl(raw.image));
  }

  if (Array.isArray(raw?.images)) {
    raw.images.forEach((entry) => pushUnique(resolveImageEntry(entry)));
  }

  return collected;
}

function pickBlogImage(raw) {
  return pickBlogImages(raw)[0] || "";
}

export function mapCategory(category) {
  if (!category) return null;
  if (typeof category === "string") {
    return { id: category, slug: category, name: category };
  }
  return {
    id: category._id || category.id || "",
    slug: category.slug || "",
    name: category.name || category.slug || "",
    description: category.description || "",
    icon: category.icon || "",
    orderIndex: category.orderIndex,
  };
}

export function mapBlog(raw) {
  if (!raw) return null;

  const category = mapCategory(raw.category);
  const image = pickBlogImage(raw);

  const readTimeValue = raw.readTime;
  const readTime =
    readTimeValue === undefined || readTimeValue === null || readTimeValue === ""
      ? ""
      : typeof readTimeValue === "number"
        ? `${readTimeValue} min read`
        : String(readTimeValue);

  const gallery = pickBlogImages(raw);
  const cover = image || gallery[0] || "";

  return {
    id: raw._id || raw.id,
    slug: raw.slug,
    title: raw.title || "",
    excerpt: raw.description || raw.excerpt || "",
    content: raw.content,
    featuredImage: cover,
    // Listing cards can fall back; detail page only shows real covers
    cardImage: cover || fallbackImage,
    author: raw.author || "Azzir Editorial",
    publishDate: raw.blogDate || raw.publishDate || raw.createdAt,
    readTime,
    category: category?.slug || "",
    categoryId: category?.id || "",
    categoryName: category?.name || "",
    categoryMeta: category,
    seo: {
      title: raw.seoTitle || raw.seo?.title || "",
      description: raw.seoDescription || raw.seo?.description || "",
      keywords:
        raw.primaryKeyword ||
        (Array.isArray(raw.seoKeywords) ? raw.seoKeywords.filter(Boolean).join(", ") : "") ||
        raw.seo?.keywords ||
        "",
    },
    status: raw.status,
    orderIndex: raw.orderIndex,
    images: gallery,
    tags: Array.isArray(raw.tags) ? raw.tags.filter(Boolean) : [],
    raw,
  };
}

export function mapBlogList(payload) {
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return {
    items: items.map(mapBlog).filter(Boolean),
    total: Number(payload?.total ?? items.length) || 0,
    perPage: Number(payload?.perPage ?? items.length) || items.length || 12,
    cursor: payload?.cursor,
    remainingCount: payload?.remaningCount ?? payload?.remainingCount,
  };
}

export function mapBlogsHome(payload) {
  const data = payload?.data || payload || {};
  const categories = (data.categories || []).map(mapCategory).filter(Boolean);
  const latest = (data.latest || []).map(mapBlog).filter(Boolean);

  return {
    hero: {
      badge: data.hero?.badge || "BLOG",
      title: data.hero?.title || "Discover. Read. Get Inspired.",
      subtitle:
        data.hero?.subtitle ||
        "Nightlife guides, club picks, events and stories to help you plan better nights out with Azzir.",
    },
    categories: [{ id: "all", slug: "all", name: "All" }, ...categories],
    latest,
  };
}
